import chatgptLogo from '@/assets/icons/platforms/chatgpt.svg';
import claudeLogo from '@/assets/icons/platforms/claude.svg';
import api from '@/libs/axiosInstance';
import type { McpClientProvider, McpConnection } from '@fluenceur/types';

/** The assistants a brand can connect from the dashboard. */
export type ConnectableAssistant = Extract<McpClientProvider, 'claude' | 'chatgpt'>;

export const CONNECTABLE_ASSISTANTS: ConnectableAssistant[] = ['claude', 'chatgpt'];

export interface AssistantProvider {
  provider: ConnectableAssistant;
  name: string;
  logo: string;
}

export const ASSISTANT_PROVIDERS: Record<ConnectableAssistant, AssistantProvider> = {
  claude: { provider: 'claude', name: 'Claude', logo: claudeLogo },
  chatgpt: { provider: 'chatgpt', name: 'ChatGPT', logo: chatgptLogo },
};

/** The name the connector carries inside the assistant. */
const CONNECTOR_NAME = 'Fluenceur';

/** The address an assistant connects to: the API's own MCP endpoint, on this environment. */
export const mcpServerUrl = (): string => `${String(api.defaults.baseURL).replace(/\/$/, '')}/mcp`;

/**
 * Claude's documented install link opens its "Add custom connector" dialog with the name
 * and address already filled in; the brand only confirms. The `admin-settings` path is
 * the same dialog for an Owner adding the connector to a whole Team or Enterprise
 * organisation, whose members cannot add one themselves.
 */
export const claudeInstallUrl = (scope: 'personal' | 'organization'): string => {
  const path = scope === 'organization' ? 'admin-settings' : 'customize';
  const params = new URLSearchParams({
    modal: 'add-custom-connector',
    connectorName: CONNECTOR_NAME,
    connectorUrl: mcpServerUrl(),
  });
  return `https://claude.ai/${path}/connectors?${params.toString()}`;
};

/**
 * ChatGPT documents no link that prefills a custom app: the closest pages are the
 * setting that turns developer mode on, and the page whose "+" creates the app.
 */
export const CHATGPT_DEVELOPER_MODE_URL = 'https://chatgpt.com/#settings/Security';
export const CHATGPT_ADD_APP_URL = 'https://chatgpt.com/plugins';

/** The live connections one assistant holds, and the brands they cover, each named once. */
export const assistantConnectionSummary = (
  connections: McpConnection[] | undefined,
  provider: ConnectableAssistant
): { isConnected: boolean; brandNames: string[] } => {
  const live = (connections ?? []).filter(
    (connection) => !connection.revokedAt && connection.client.provider === provider
  );
  const brandNames = [
    ...new Set(live.flatMap((connection) => connection.brands.map((brand) => brand.name))),
  ];
  return { isConnected: live.length > 0, brandNames };
};
